// src/lib/firebase.js
import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";
import { getFirestore, initializeFirestore } from "firebase/firestore";

// --- Read CRA envs (REACT_APP_*) ---
const STORAGE_BUCKET =
  process.env.REACT_APP_FB_STORAGE_BUCKET ||
  (process.env.REACT_APP_FB_PROJECT_ID
    ? `${process.env.REACT_APP_FB_PROJECT_ID}.appspot.com`
    : "");

const cfg = {
  apiKey: process.env.REACT_APP_FB_API_KEY,
  authDomain: process.env.REACT_APP_FB_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FB_PROJECT_ID,
  storageBucket: STORAGE_BUCKET,
  appId: process.env.REACT_APP_FB_APP_ID,
  messagingSenderId: process.env.REACT_APP_FB_MESSAGING_SENDER_ID,
};

// --- Check for missing values ---
const missing = Object.entries(cfg)
  .filter(([, v]) => !v)
  .map(([k]) => k);

export const __FIREBASE_CONFIG_OK__ = missing.length === 0;

// Friendly console error (but do NOT throw)
if (!__FIREBASE_CONFIG_OK__) {
  const required = [
    "REACT_APP_FB_API_KEY",
    "REACT_APP_FB_AUTH_DOMAIN",
    "REACT_APP_FB_PROJECT_ID",
    "REACT_APP_FB_STORAGE_BUCKET",
    "REACT_APP_FB_APP_ID",
    "REACT_APP_FB_MESSAGING_SENDER_ID",
  ];
  // eslint-disable-next-line no-console
  console.error(
    [
      "[Elecat Firebase] Missing Firebase env in .env.local.",
      "Required REACT_APP_* keys:",
      ...required.map((k) => `  - ${k}`),
      "",
      `Missing now: ${missing.join(", ") || "<none>"}`,
      "",
      "Fix:",
      "1) Add the keys above to .env.local at project root (no quotes).",
      "2) Stop and re-run `npm start` (env changes need a full restart).",
    ].join("\n")
  );
}

// --- Small guard so any accidental usage explains itself nicely ---
function guard(feature) {
  if (!__FIREBASE_CONFIG_OK__) {
    throw new Error(
      `Firebase is not configured. Tried to use: ${feature}.\n` +
        "Add REACT_APP_* vars to .env.local and restart `npm start`."
    );
  }
}

// --- Initialize only when config is OK ---
let app = null;
let db = null;
let auth = null;

if (__FIREBASE_CONFIG_OK__) {
  app = getApps().length ? getApp() : initializeApp(cfg);
  // Use Firestore with long-polling auto-detection to avoid extension/network blocks
  try {
    db = initializeFirestore(app, {
      experimentalForceLongPolling: true,
      experimentalAutoDetectLongPolling: true,
      useFetchStreams: false,
    });
  } catch {
    db = getFirestore(app);
  }
  auth = getAuth(app);
  setPersistence(auth, browserLocalPersistence).catch(() => {});
  // Optional dev helper: bypass reCAPTCHA for test phone numbers only
  try {
    if (process.env.REACT_APP_FB_DISABLE_RECAPTCHA_TEST === "1") {
      // This only affects numbers configured in Firebase Console as test numbers.
      // Do NOT enable in production.
      // eslint-disable-next-line no-unused-expressions
      auth.settings && (auth.settings.appVerificationDisabledForTesting = true);
    }
  } catch {}
}

// --- Safe wrappers that guard when config is missing ---
export { app, db, auth };

export function getOrCreateRecaptcha(
  containerId = "recaptcha-container",
  size = "invisible",
  onSolved
) {
  guard("RecaptchaVerifier");
  if (window._recaptchaVerifier) return window._recaptchaVerifier;

  if (!document.getElementById(containerId)) {
    const div = document.createElement("div");
    div.id = containerId;
    if (size === "invisible") {
      div.style.position = "absolute";
      div.style.left = "-9999px";
    }
    document.body.appendChild(div);
  }

  const verifier = new RecaptchaVerifier(auth, containerId, {
    size,
    callback: () => typeof onSolved === "function" && onSolved(),
    "expired-callback": async () => {
      try {
        const widgetId = await verifier.render();
        if (window.grecaptcha?.reset) window.grecaptcha.reset(widgetId);
      } catch {}
    },
  });

  verifier.render().catch(() => {});
  window._recaptchaVerifier = verifier;
  return verifier;
}

export async function signInWithPhone(e164PhoneNumber, recaptcha) {
  guard("signInWithPhoneNumber");
  const verifier = recaptcha || getOrCreateRecaptcha();
  return signInWithPhoneNumber(auth, e164PhoneNumber, verifier);
}

export async function disposeRecaptcha() {
  if (!window._recaptchaVerifier) return;
  try {
    const id = await window._recaptchaVerifier.render();
    if (window.grecaptcha?.reset) window.grecaptcha.reset(id);
  } catch {}
  window._recaptchaVerifier = undefined;
}
